import { useState, useEffect } from 'react';
import { CreditCard, TrendingUp, AlertTriangle, ExternalLink, Calendar, Users } from 'lucide-react';
import { API_BASE_URL } from '@/config';
import { useTranslation } from '@/i18n';

interface SubscriptionData {
  plan: string;
  status: string;
  conversationsUsed: number;
  conversationsLimit: number;
  currentPeriodEnd?: Date;
  billingInterval?: 'monthly' | 'yearly';
  cancelAtPeriodEnd?: boolean;
  usagePercentage: number;
  planDisplayName: string;
  isNearLimit: boolean;
  isOverLimit: boolean;
}

interface BillingCardProps {
  tenantId: string;
}

export default function BillingCard({ tenantId }: BillingCardProps) {
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, [tenantId]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/billing/subscription`, {
        headers: {
          'X-Tenant-Id': tenantId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
      } else {
        console.error('Failed to fetch subscription');
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: string) => {
    try {
      setUpgrading(true);
      
      // Choose billing interval (default to monthly)
      const billingInterval = 'monthly';
      
      const response = await fetch(`${API_BASE_URL}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
        },
        body: JSON.stringify({
          plan,
          billingInterval,
          successUrl: `${window.location.origin}/settings?success=true`,
          cancelUrl: `${window.location.origin}/settings?canceled=true`,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.sessionUrl;
      } else {
        console.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
        },
        body: JSON.stringify({
          returnUrl: window.location.href,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Customer Portal
        window.location.href = data.portalUrl;
      } else {
        console.error('Failed to create portal session');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('billing.unableToLoad')}</h3>
          <p className="text-gray-500 mb-4">{t('billing.unableToLoadDesc')}</p>
          <button
            onClick={fetchSubscription}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
          >
            {t('billing.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: t('billing.statusActive') },
      trialing: { color: 'bg-blue-100 text-blue-800', label: t('billing.statusTrial') },
      canceled: { color: 'bg-gray-100 text-gray-800', label: t('billing.statusCanceled') },
      past_due: { color: 'bg-red-100 text-red-800', label: t('billing.statusPastDue') },
      incomplete: { color: 'bg-yellow-100 text-yellow-800', label: t('billing.statusIncomplete') },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.canceled;
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getUsageBarColor = () => {
    if (subscription.isOverLimit) return 'bg-red-500';
    if (subscription.isNearLimit) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('billing.currentPlan')}</h3>
            <div className="flex items-center space-x-3">
              <span className="text-2xl font-bold text-gray-900">
                {subscription.planDisplayName}
              </span>
              {getStatusBadge(subscription.status)}
            </div>
          </div>
          <CreditCard className="h-8 w-8 text-gray-400" />
        </div>

        {/* Usage Statistics */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">{t('billing.conversationsUsed')}</span>
            <span className="text-sm text-gray-500">
              {subscription.conversationsUsed.toLocaleString()} / {subscription.conversationsLimit.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getUsageBarColor()}`}
              style={{ width: `${Math.min(subscription.usagePercentage, 100)}%` }}
            ></div>
          </div>
          {subscription.isNearLimit && (
            <div className="flex items-center mt-2 text-sm text-yellow-600">
              <AlertTriangle className="h-4 w-4 mr-1" />
              {subscription.isOverLimit ? t('billing.usageLimitExceeded') : t('billing.approachingLimit')}
            </div>
          )}
        </div>

        {/* Subscription Details */}
        {subscription.plan !== 'free' && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {subscription.currentPeriodEnd && (
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">{t('billing.nextBilling')}</span>
                  <span className="font-medium text-gray-900">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </div>
              )}
              {subscription.billingInterval && (
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">{t('billing.billingPeriod')}</span>
                  <span className="font-medium text-gray-900 capitalize">
                    {subscription.billingInterval}
                  </span>
                </div>
              )}
            </div>
            
            {subscription.cancelAtPeriodEnd && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center text-yellow-800">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <span className="text-sm">
                    {t('billing.cancelNotice')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {subscription.plan === 'free' ? (
            <>
              <button
                onClick={() => handleUpgrade('starter')}
                disabled={upgrading}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              >
                {upgrading ? t('billing.processing') : t('billing.upgradeToStarter')}
              </button>
              <button
                onClick={() => handleUpgrade('pro')}
                disabled={upgrading}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              >
                {upgrading ? t('billing.processing') : t('billing.upgradeToPro')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleManageSubscription}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center flex-1"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('billing.manageSubscription')}
              </button>
              {subscription.plan === 'starter' && (
                <button
                  onClick={() => handleUpgrade('pro')}
                  disabled={upgrading}
                  className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                >
                  {upgrading ? t('billing.processing') : t('billing.upgradeToPro')}
                </button>
              )}
              {(subscription.plan === 'starter' || subscription.plan === 'pro') && (
                <button
                  onClick={() => handleUpgrade('business')}
                  disabled={upgrading}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                >
                  {upgrading ? t('billing.processing') : t('billing.upgradeToBusiness')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Plan Comparison */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('billing.planComparison')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { name: 'Free', conversations: '50', projects: '1', price: '$0' },
            { name: 'Starter', conversations: '500', projects: '1', price: '$29' },
            { name: 'Pro', conversations: '3,000', projects: '3', price: '$99' },
            { name: 'Business', conversations: '10,000', projects: '10', price: '$299' },
          ].map((plan) => (
            <div
              key={plan.name}
              className={`border rounded-lg p-4 ${
                subscription.planDisplayName === plan.name
                  ? 'border-indigo-200 bg-indigo-50'
                  : 'border-gray-200'
              }`}
            >
              <div className="text-center">
                <h4 className="font-medium text-gray-900">{plan.name}</h4>
                <div className="text-2xl font-bold text-gray-900 my-2">{plan.price}</div>
                <div className="text-sm text-gray-500 mb-3">{t('billing.perMonth')}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>{t('billing.conversations')}</span>
                    <span className="font-medium">{plan.conversations}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>{t('billing.projects')}</span>
                    <span className="font-medium">{plan.projects}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}